// WaPauseBanner — "tus mensajes automáticos están en pausa", en TODAS las pantallas
// (F3, 9-sep-2026). Mismo sitio y mismo patrón que PlanReadOnlyBanner: si la pausa solo
// se viera en la página de WhatsApp, alguien preguntaría "¿por qué no le llegaron sus
// puntos?" y nadie sabría contestar. Se relee cada minuto para desaparecer solo cuando
// la pausa caduca.
import React, { useCallback, useEffect, useState } from 'react';
import { PauseCircle, Loader2 } from 'lucide-react';
import whatsappApi from '../api/whatsappApi';
import { leerPausa, hastaTexto, WA_PAUSE_EVENT, PAUSE_OFF, type WaPauseState } from '../features/whatsapp/waPauseShared';

const WaPauseBanner: React.FC = () => {
  const [st, setSt] = useState<WaPauseState>(PAUSE_OFF);
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(() => { leerPausa().then(setSt); }, []);
  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 60000);
    window.addEventListener(WA_PAUSE_EVENT, cargar);
    return () => { clearInterval(t); window.removeEventListener(WA_PAUSE_EVENT, cargar); };
  }, [cargar]);

  if (!st.activa) return null;

  const reanudar = async () => {
    setBusy(true);
    try { await whatsappApi.clearPause(); setSt(PAUSE_OFF); } catch { /* el botón de la página lo reintenta */ }
    finally { setBusy(false); }
  };

  return (
    <div
      role="status"
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-500/10"
    >
      <div className="flex items-start gap-3">
        <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {st.level === 1
              ? `Campañas y avisos masivos en pausa ${hastaTexto(st.until)}`
              : `Mensajes automáticos en pausa ${hastaTexto(st.until)}`}
          </p>
          <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-300/90">
            {st.level === 1
              ? 'No saldrán campañas ni felicitaciones de cumpleaños. Todo lo demás sigue igual.'
              : 'No saldrán puntos, cumpleaños, gift cards ni campañas. Lo que envías tú desde el panel sí sale.'}
            {st.by ? ` · la puso ${st.by}` : ''}
          </p>
        </div>
        <button
          onClick={reanudar}
          disabled={busy}
          className="shrink-0 text-sm font-semibold text-amber-900 underline underline-offset-2 disabled:opacity-60 dark:text-amber-200"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Reanudar'}
        </button>
      </div>
    </div>
  );
};

export default WaPauseBanner;
